/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@/components/button'
import { MainContent } from '@/components/content'
import { Close, Filter, UserAddIcon } from '@/components/icons'
import { AutocompleteAddress, AutocompleteLocationInput } from '@/components/input'
import { MainLayout } from '@/components/layouts'

import { InvitePlayerModal, SubscribeModal } from '@/components/modal'
import { RangeSlider } from '@/components/range-slider'
import { Search } from '@/components/search'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import GoogleAd from '@/components/sidebar/GoogleAd'
import { PlayerItemLoading } from '@/components/skeleton'
import { eventTracker } from '@/config/trackingFunctions'
import { LIMIT, OFFSET, SCROLLING_HEIGHT_ABS_ERROR, SCROLLING_THROTTLING_DURATION } from '@/constants/Common'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { useRemoteConfig } from '@/hooks/useRemoteConfig'
import { useUserDetails } from '@/hooks/useUserDetails'
import { authSelector } from '@/pages/auth/selectors'
import { appSelector } from '@/pages/home/selectors'
import { claimPlayer, receiveFilter, receiveSearchText, searchPlayer } from '@/pages/player/playerSlice'
import { playerSelector } from '@/pages/player/selectors'
import { userSelector } from '@/pages/user/selectors'
import { getPlaceDetail } from '@/services/address'
import { FilterSearchPlayer, Gender } from '@/types'
import { ProfileUnlockDisclosureProvider } from '@/components/profile-limit'
import { useProfileViewsLimited } from '@/hooks/useProfileViews'
import { useUpgradeCta } from '@/hooks/useUpgradeCta'
import { throttle } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'
import { PlayerItem } from '../components'

export const Player = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const { token } = useAppSelector(authSelector)
  const { resSearchPlayer, isLoading, searchText, filter } = useAppSelector(playerSelector)

  const { getNumber } = useRemoteConfig()
  const lastSearchEventTime = useRef(0)

  const ratingFilterLocked = useProfileViewsLimited()
  const { openUpgrade, subscribeOpen, closeSubscribe } = useUpgradeCta('profile_limit_cta_clicked')

  const isMobile = useMediaQuery('(max-width: 767px)')

  const userData = useUserDetails()

  const [isShowFilter, setIsShowFilter] = useState(false)
  const [geometry, setGeometry] = useState<{ lat: number; lng: number } | null>(null)
  const [location, setLocation] = useState<AutocompleteAddress>({ description: '', placeId: '' })
  const [gender, setGender] = useState<Gender | ''>('')
  const [ageRange, setAgeRange] = useState<number[]>([])
  const [ratingRange, setRatingRange] = useState<number[]>([])
  const [doubles, setDoubles] = useState(true)
  const [singles, setSingles] = useState(true)
  const [distance, setDistance] = useState(0)
  const [offset, setOffset] = useState(OFFSET)
  const [hasFilter, setHasFilter] = useState(false)
  const [showInvitePlayerModal, setShowInvitePlayerModal] = useState(false)

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)
  const debouncedSearchEventText = useDebounce<string>(searchText || '*', 500)

  const toggleFilter = () => setIsShowFilter(!isShowFilter)

  const toggleInvitePlayer = () => setShowInvitePlayerModal(!showInvitePlayerModal)

  const onGetPlayer = () => {
    if (user)
      dispatch(
        searchPlayer({
          limit: LIMIT,
          offset: offset,
          query: debouncedSearchText ? debouncedSearchText : '*',
          exclude: [],
          includeUnclaimedPlayers: true,
          filter: filter,
        })
      )

    if (searchText && searchText.length > 2 && debouncedSearchEventText === searchText) {
      const cooldown = getNumber('searchEventCooldownSeconds', 0) * 1000
      const now = Date.now()
      if (now - lastSearchEventTime.current > cooldown) {
        eventTracker(TRACKING_TYPES.SEARCH_PERFORMED, {
          ...userData,
          'Page Name': PAGE_NAMES.BROWSE_PLAYERS,
          Keyword: searchText,
        })
        lastSearchEventTime.current = now
      }
    }
  }

  const onClaimPlayer = () => {
    const claimFilter: FilterSearchPlayer = {
      ...(filter.lat && { lat: filter.lat }),
      ...(filter.lng && { lng: filter.lng }),
      ...(filter.rating?.maxRating && { rating: filter.rating }),
      ...(filter.ageRange && { age: filter.ageRange }),
      ...(filter.gender && { gender: filter.gender }),
      ...(filter.lat && filter.lng && { radiusInMeters: 40233.6 }),
    }
    dispatch(
      claimPlayer({
        filter: claimFilter,
        limit: LIMIT,
        offset: offset,
        query: debouncedSearchText ? debouncedSearchText : '*',
      })
    )
  }

  const handleScroll = throttle(() => {
    if (
      Math.abs(window.innerHeight + document.documentElement.scrollTop - document.documentElement.offsetHeight) >
      SCROLLING_HEIGHT_ABS_ERROR
    ) {
      return
    }

    setOffset((pre) => pre + LIMIT)
  }, SCROLLING_THROTTLING_DURATION)

  const onApplyFilter = () => {
    if (
      location.description !== '' ||
      gender !== '' ||
      ageRange.length > 0 ||
      ratingRange.length > 0 ||
      !singles ||
      !doubles ||
      distance > 0
    ) {
      setHasFilter(true)
    } else {
      setHasFilter(false)
    }

    if (user) {
      const filterChange: FilterSearchPlayer = {
        ...filter,
        lat: geometry ? geometry.lat : user?.addresses?.[0]?.latitude,
        lng: geometry ? geometry.lng : user?.addresses?.[0]?.longitude,
        ...(doubles &&
          singles && {
            rating: {
              minRating: ratingRange[0],
              maxRating: ratingRange[1],
            },
          }),
        ...(!doubles &&
          !singles && {
            rating: {
              maxRating: null,
              minRating: null,
            },
          }),
        ...(doubles &&
          !singles && {
            rating: {
              minRating: ratingRange[0],
              maxRating: ratingRange[1],
              type: 'DOUBLES',
            },
          }),
        ...(!doubles &&
          singles && {
            rating: {
              minRating: ratingRange[0],
              maxRating: ratingRange[1],
              type: 'SINGLES',
            },
          }),
        ...(gender && {
          gender,
        }),
        ...(distance && {
          radiusInMeters: distance * 1609.34,
        }),
        ...(ageRange.length && {
          ageRange: {
            minAge: ageRange[0] === 18 ? 0 : ageRange[0],
            maxAge: ageRange[1] === 81 ? 105 : ageRange[1],
          },
        }),
      }
      dispatch(receiveFilter({ ...filterChange, locationText: location?.description as string }))
    }
    if (!user) {
      const filterChange: FilterSearchPlayer = {
        ...filter,
        ...(geometry?.lat && { lat: geometry.lat }),
        ...(geometry?.lng && { lng: geometry.lng }),
        ...(doubles &&
          singles && {
            rating: {
              minRating: ratingRange[0],
              maxRating: ratingRange[1],
            },
          }),
        ...(!doubles &&
          !singles && {
            rating: {
              maxRating: null,
              minRating: null,
            },
          }),
        ...(doubles &&
          !singles && {
            rating: {
              minRating: ratingRange[0],
              maxRating: ratingRange[1],
              type: 'DOUBLES',
            },
          }),
        ...(!doubles &&
          singles && {
            rating: {
              minRating: ratingRange[0],
              maxRating: ratingRange[1],
              type: 'SINGLES',
            },
          }),
        ...(gender && {
          gender,
        }),
        ...(distance && {
          radiusInMeters: distance * 1609.34,
        }),
        ...(ageRange.length && {
          ageRange: {
            minAge: ageRange[0] === 18 ? 0 : ageRange[0],
            maxAge: ageRange[1] === 81 ? 105 : ageRange[1],
          },
        }),
      }
      dispatch(receiveFilter({ ...filterChange, locationText: location?.description as string }))
    }
    setOffset(OFFSET)
  }

  const onClearAll = () => {
    setHasFilter(false)
    if (user) {
      dispatch(
        receiveFilter({
          lat: user?.addresses[0].latitude,
          lng: user?.addresses[0].longitude,
          rating: {
            maxRating: null,
            minRating: null,
          },
          locationText: '',
        })
      )
      setDistance(0)
      setGender('')
      setRatingRange([])
      setAgeRange([])
      setDoubles(true)
      setSingles(true)
      setLocation({ description: '', placeId: '' })
      setGeometry(null)
    }
    setOffset(OFFSET)
  }

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(receiveSearchText(e.target.value))
    setOffset(OFFSET)
  }

  const onGetPlaceDetail = async (address: AutocompleteAddress) => {
    const res = await getPlaceDetail(address?.placeId)
    setGeometry({ lat: res.result.geometry.location.lat, lng: res.result.geometry.location.lng })
  }

  useEffect(() => {
    if (resSearchPlayer.hasMore) window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [resSearchPlayer])

  useEffect(() => {
    if (user && filter.lat && filter.lng) onGetPlayer()
    if (!user) onClaimPlayer()
  }, [offset, debouncedSearchText, debouncedSearchEventText, filter, user, token])

  useEffect(() => {
    if (filter.locationText) {
      setHasFilter(!!filter.locationText)
      setLocation({
        description: filter.locationText,
        placeId: '',
      })
    }
    if (filter.radiusInMeters) {
      setHasFilter(!!filter.radiusInMeters)
      setDistance(filter?.radiusInMeters / 1609.34)
    }
    if (filter.gender) {
      setHasFilter(!!filter.gender)
      setGender(filter?.gender)
    }
    if (filter.ageRange) {
      setHasFilter(!!filter.ageRange)
      setAgeRange([filter.ageRange.minAge, filter.ageRange.maxAge])
    }
    if (filter.rating?.maxRating && filter.rating.minRating) {
      setHasFilter(!!filter.rating)
      setRatingRange([filter.rating.minRating, filter.rating.maxRating])
    }
    if (!filter.rating?.type) {
      setDoubles(true)
      setSingles(true)
    }
    if (filter.rating?.type === 'DOUBLES') {
      setHasFilter(true)
      setDoubles(true)
      setSingles(false)
    }
    if (filter.rating?.type === 'SINGLES') {
      setHasFilter(true)
      setSingles(true)
      setDoubles(false)
    }
  }, [filter])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent isShowFilter={isShowFilter}>
          <div className="md:flex md:flex-row md:justify-between">
            <div className={`flex flex-col mb-20 lg:mb-0 w-full`}>
              <h1 className="text-2xl md:text-[32px] md:leading-10 font-bold text-white">
                {t('player.searchPlayers')}
              </h1>

              <div
                className={`flex flex-col sm:flex-row sm:items-center gap-y-3 justify-between xl:max-w-[640px] 2xl:max-w-[720px] mt-4 md:mt-3 ${
                  isShowFilter ? 'lg:max-w-[432px]' : 'lg:max-w-[640px]'
                }`}
              >
                <div className="flex items-center justify-between md:justify-start gap-x-2 md:gap-x-4">
                  <Search searchText={searchText} onChangeInput={onChangeInput} className="py-2 px-4 md:w-52 gap-x-2" />
                  <Button onClick={toggleFilter} className={`${hasFilter ? 'bg-white' : ''} hover:opacity-50`}>
                    <Filter className={`${hasFilter ? 'text-navy-900' : 'text-white'}`} />
                  </Button>
                </div>
                <button
                  className="flex items-center justify-between sm:justify-start bg-primary-500 py-2 px-3 rounded-full text-sm text-white font-semibold gap-x-2 hover:bg-opacity-70"
                  onClick={toggleInvitePlayer}
                >
                  <UserAddIcon className="w-4 h-4 text-white order-2 sm:order-1" />
                  <span className="order-1 sm:order-2">{t('common.invite')}</span>
                </button>
              </div>

              <ProfileUnlockDisclosureProvider>
                <div className="flex flex-col gap-y-2 mt-6 md:mt-4">
                  {resSearchPlayer?.hits?.map((item, i) =>
                    isMobile ? (
                      (i + 3) % 5 === 0 ? (
                        <GoogleAd format="fluid" layoutKey="-6t+ed+2i-1n-4w" slot="5800496363" />
                      ) : (
                        <PlayerItem key={item.id} player={item} isFilter={isShowFilter} enableProfileUnlock />
                      )
                    ) : (
                      <PlayerItem key={item.id} player={item} isFilter={isShowFilter} enableProfileUnlock />
                    )
                  )}
                  {isLoading && (
                    <>
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                      <PlayerItemLoading isFilter={isShowFilter} />
                    </>
                  )}
                </div>
              </ProfileUnlockDisclosureProvider>
              {resSearchPlayer && !resSearchPlayer?.hasMore && (
                <div className="flex justify-center">
                  <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
                </div>
              )}
            </div>

            {isShowFilter && (
              <div
                className={`fixed z-50 top-0 w-screen h-screen p-4 lg:p-0 lg:pb-4 lg:px-2 bg-primary-400 lg:bg-transparent left-0 lg:left-auto lg:right-6 xl:right-9 lg:top-36 lg:w-[256px] xl:w-[296px] lg:max-h-[calc(100vh-144px)] overflow-y-auto overflow-x-hidden transition-all duration-500 ease-in-out flex flex-col gap-y-6`}
              >
                <div className="flex justify-between py-4">
                  <div className="flex items-center gap-x-2">
                    <Button onClick={toggleFilter} className="hover:opacity-50">
                      <Close className="text-white" />
                    </Button>
                    <span className="text-xl font-semibold text-white">{t('common.filters')}</span>
                  </div>
                  <button className="text-sm px-1 text-white font-semibold" onClick={onApplyFilter}>
                    {t('common.apply')}
                  </button>
                  <button className="text-sm px-1 text-white font-semibold" onClick={onClearAll}>
                    {t('common.clearAll')}
                  </button>
                </div>

                <AutocompleteLocationInput
                  label={t('common.location')}
                  value={location}
                  setValue={(location: AutocompleteAddress) => {
                    setLocation(location)
                    if (!location.description) {
                      setGeometry(null)
                    }
                  }}
                  onChange={(location) => {
                    onGetPlaceDetail(location)
                  }}
                />

                {user && (
                  <div className="flex flex-col gap-y-2">
                    <span className="text-white text-sm font-bold">{t('common.distance')}</span>
                    <div className="flex justify-between items-center gap-x-4 pt-12">
                      <div className="w-full">
                        <RangeSlider
                          className="h-4"
                          min={0}
                          minLabel={0}
                          max={100}
                          value={distance}
                          onChange={(e) => setDistance(e)}
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-white text-xs font-medium text-opacity-80">{t('common.noLimit')}</span>
                          <span className="text-white text-xs font-medium text-opacity-80">100</span>
                        </div>
                      </div>
                      <span className="text-white text-xs font-medium w-7">Mi</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row lg:flex-col gap-y-6 md:gap-x-10">
                  <div className="flex flex-col flex-1 gap-y-2">
                    <span className="text-white text-sm font-bold">{t('common.gender')}</span>
                    <div className="rounded-full border h-[50px] border-primary-300 flex">
                      <button
                        className={`flex-1 rounded-full  ${
                          !gender ? 'bg-primary-300' : ''
                        } text-[13px] text-white font-medium`}
                        onClick={() => setGender('')}
                      >
                        {t('common.anybody')}
                      </button>
                      <button
                        className={`flex-1 rounded-full  ${
                          gender === Gender.MALE ? 'bg-primary-300' : ''
                        } text-[13px] text-white font-medium`}
                        onClick={() => setGender(Gender.MALE)}
                      >
                        {t('common.men')}
                      </button>
                      <button
                        className={`flex-1 rounded-full  ${
                          gender === Gender.FEMALE ? 'bg-primary-300' : ''
                        } text-[13px] text-white font-medium`}
                        onClick={() => setGender(Gender.FEMALE)}
                      >
                        {t('common.women')}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1 gap-y-2">
                    <span className="text-white text-sm font-bold">{t('common.type')}</span>
                    <div className="flex">
                      <div className="flex w-28 items-center cursor-pointer" onClick={() => setDoubles(!doubles)}>
                        <div className="p-3 flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="w-5 h-5 cursor-pointer"
                            checked={doubles}
                            onChange={(e) => setDoubles(e.target.checked)}
                          />
                        </div>
                        <span className="text-sm text-white font-semibold">{t('common.doubles')}</span>
                      </div>
                      <div className="flex w-28 items-center cursor-pointer" onClick={() => setSingles(!singles)}>
                        <div className="p-3 flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="w-5 h-5 cursor-pointer"
                            checked={singles}
                            onChange={(e) => setSingles(e.target.checked)}
                          />
                        </div>
                        <span className="text-sm text-white font-semibold">{t('common.singles')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="flex flex-col gap-y-2"
                  onClick={() => {
                    if (ratingFilterLocked) openUpgrade()
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-bold">{t('common.duprRating')}</span>
                    <span className="text-white text-sm font-bold text-opacity-80">
                      {ratingRange.length ? `${ratingRange[0]} - ${ratingRange[1]}` : `2 - 8`}
                    </span>
                  </div>
                  {ratingFilterLocked && (
                    <span className="text-white text-xs font-medium text-opacity-80">
                      {t('player.filterRatingLocked')}
                    </span>
                  )}
                  <div className={`pt-4 ${ratingFilterLocked ? 'opacity-40 pointer-events-none' : ''}`}>
                    <RangeSlider
                      className="w-full h-4"
                      defaultValue={[2, 8]}
                      step={0.5}
                      min={2}
                      max={8}
                      hiddenValue={true}
                      value={ratingRange.length ? ratingRange : [2, 8]}
                      onChange={(e) => setRatingRange(e)}
                      isArrayValue
                      minDistance={1}
                    />
                    <div className="w-full flex mt-2">
                      <span className="flex-1 text-white text-xs font-medium text-opacity-80 pl-1">2</span>
                      <span className="flex-1 text-white text-xs font-medium text-opacity-80">3</span>
                      <span className="flex-1 text-white text-xs font-medium text-opacity-80">4</span>
                      <span className="flex-1 text-white text-xs font-medium text-opacity-80">5</span>
                      <span className="flex-1 text-white text-xs font-medium text-opacity-80">6</span>
                      <span className="flex-1 text-white text-xs font-medium text-opacity-80">7</span>
                      <span className="text-white text-xs font-medium text-opacity-80 w-[10px]">8</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-y-2">
                  <span className="text-white text-sm font-bold">{t('common.ageRange')}</span>
                  <div className="pt-12">
                    <RangeSlider
                      className="w-full h-4"
                      defaultValue={[18, 81]}
                      min={18}
                      minLabel={'19-'}
                      max={81}
                      maxLabel={'80+'}
                      value={ageRange.length ? ageRange : [18, 81]}
                      onChange={(e) => setAgeRange(e)}
                      isArrayValue
                    />
                    <div className="w-full flex justify-between mt-2">
                      <span className="text-white text-xs font-medium text-opacity-80">{t('common.under19')}</span>
                      <span className="text-white text-xs font-medium text-opacity-80">80+</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </MainContent>
        <InvitePlayerModal open={showInvitePlayerModal} onClose={toggleInvitePlayer} />
        <SubscribeModal open={subscribeOpen} onClose={closeSubscribe} />
      </MainLayout>
    </>
  )
}
